import { useNexaStore } from '../NexaStore';

interface FnCallConfig {
  id: string;
  name?: string;
}

// Configuration for mapping function names to their dashboard config lookup strategies
// Add new function name mappings here to automatically integrate them
// toolName can be a string or null (if null, any tool_name will match)
const FUNCTION_CONFIG_MAPPING = {
  handle_sessions: {
    toolName: null, // Match any tool_name for this function (handles multiple tools)
    useParentCallId: true,
  },
  viewer_analysis_tool: {
    toolName: null, // Match any tool_name for this function (handles multiple tools)
    useParentCallId: true,
  },
  broken_assets_and_new_app_versions_tool: {
    toolName: null, // Match any tool_name (handles both fetch_app_versions_csv and fetch_broken_assets_csv)
    useParentCallId: true,
  },
  handle_alerts: {
    toolName: 'alerts_query',
    useParentCallId: true,
  },
  handle_video_alerts: {
    toolName: null,
    useParentCallId: true,
  },
  generate_static_report: {
    toolName: 'generate_static_report',
    useParentCallId: true,
  },
};

const useGetDashboardConfig = (fnCallConfig: FnCallConfig) => {
  const dashboardConfigMap = useNexaStore((state) => state.dashboardConfigMap);
  if (!dashboardConfigMap) {
    return null;
  }

  // Handle function calls with special lookup strategies
  if (fnCallConfig?.name && FUNCTION_CONFIG_MAPPING[fnCallConfig.name]) {
    const { toolName, useParentCallId } = FUNCTION_CONFIG_MAPPING[fnCallConfig.name];
    const matchingEntry = Object.entries(dashboardConfigMap).find(([_, config]: [string, any]) => {
      // Check if tool_name matches (if toolName is null, match any tool_name)
      const toolMatches = toolName === null || config?.tool_name === toolName;

      // If useParentCallId is true, also check parent_call_id
      const parentMatches = useParentCallId ? config?.parent_call_id === fnCallConfig.id : true;
      return toolMatches && parentMatches;
    });

    if (matchingEntry) {
      return matchingEntry[1]; // Return the config object
    }
  }

  // Standard behavior: direct lookup by id
  if (dashboardConfigMap[fnCallConfig.id]) {
    return dashboardConfigMap[fnCallConfig.id];
  }

  return null;
};

export default useGetDashboardConfig;
